import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiQueryOption } from '../../../../shared/constants/api';
import { $remediatedDocumentRequest } from '../../../../shared/constants/scannable-document';
import { $sortingOrder } from '../../../../shared/constants/sort';
import {
  IAutoRemediationReportByRule,
  IScannableDocumentReportByRule,
  IScannableDocumentReportQuery,
} from '../../../../shared/interfaces/scannable-document-report.interface';
import { IScannableDocumentRule } from '../../../../shared/interfaces/scannable-document-rule.interface';
import {
  IAutoRemediationStateResponse,
  IEditScannableDocumentRequest,
  IRemediatedDocumentRequest,
  IScannableDocument,
  IScannableDocumentPdfPagesTestStateResponse,
  IScannableDocumentsResponse,
  IScannableDocumentUploadRequest,
} from '../../../../shared/interfaces/scannable-document.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { ResourceUtility } from '../../utility/resource.utility';
import { RestBuilder } from '../helpers/rest.builder';
import { RestService } from '../rest.service';

@Injectable({ providedIn: 'root' })
export class ScannableDocumentApi {
  private restBuilder: RestBuilder;

  constructor(
    private httpClient: HttpClient,
    private restService: RestService,
  ) {
    this.restBuilder = new RestBuilder();
  }

  // GET /workspaces/:workspaceId/scannable-documents
  public getScannableDocuments(
    workspaceId: string,
    limit: number,
    page: number,
    term: string,
    sortBy: string = null,
    sortOrder: $sortingOrder = null,
    documentIds: string[] = null,
  ): Observable<IScannableDocumentsResponse> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().getApiUrl();

    const params: { [param: string]: string | readonly string[] } = {};

    if (typeof limit === 'number') {
      params[ApiQueryOption.limit] = String(limit);
    }

    if (typeof page === 'number') {
      params[ApiQueryOption.page] = String(page);
    }

    if (!SharedCommonUtility.isNullish(sortBy)) {
      params[ApiQueryOption.sortBy] = sortBy;
    }

    if (!SharedCommonUtility.isNullish(sortOrder)) {
      params[ApiQueryOption.sortOrder] = sortOrder;
    }

    if (term?.length > 0) {
      params[ApiQueryOption.term] = window.encodeURIComponent(term.trim());
    }

    if (documentIds?.length > 0) {
      params[ApiQueryOption.documentIds] = documentIds.join(',');
    }

    const httpParams: HttpParams = new HttpParams({
      fromObject: params,
    });

    return this.httpClient.get<IScannableDocumentsResponse>(url, { params: httpParams });
  }

  // GET /workspaces/:workspaceId/scannable-documents/:scannableDocumentId
  public getScannableDocument(workspaceId: string, scannableDocumentId: string): Observable<IScannableDocument> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments(scannableDocumentId).getApiUrl();

    return this.httpClient.get<IScannableDocument>(url);
  }

  // PUT /workspaces/:workspaceId/scannable-documents/:scannableDocumentId
  public editScannableDocument(
    workspaceId: string,
    scannableDocumentId: string,
    editRequest: IEditScannableDocumentRequest,
  ): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments(scannableDocumentId).getApiUrl();

    return this.httpClient.put<void>(url, editRequest);
  }

  // GET /workspaces/:workspaceId/scannableDocuments/:scannableDocumentId/scannableDocumentRules/:scannableDocumentRuleId
  public getScannableDocumentReportByRule(
    workspaceId: string,
    scannableDocumentId: string,
    scannableDocumentRuleId: string,
  ): Observable<IScannableDocumentReportByRule> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .scannableDocumentsRule(scannableDocumentRuleId)
      .getApiUrl();

    return this.httpClient.get<IScannableDocumentReportByRule>(url);
  }

  // GET /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/auto-remediation-report-by-rule/:scannableDocumentRuleId
  public getAutoRemediationReportByRule(
    workspaceId: string,
    scannableDocumentId: string,
    scannableDocumentRuleId: string,
  ): Observable<IAutoRemediationReportByRule> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .autoRemediationReportByRule(scannableDocumentRuleId)
      .getApiUrl();

    return this.httpClient.get<IAutoRemediationReportByRule>(url);
  }

  // GET /workspaces/:workspaceId/scannable-documents/scannableDocumentRule/:scannableDocumentRuleId
  public getRuleById(workspaceId: string, scannableDocumentRuleId: string): Observable<IScannableDocumentRule> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments()
      .scannableDocumentsRule(scannableDocumentRuleId)
      .getApiUrl();

    return this.httpClient.get<IScannableDocumentRule>(url);
  }

  public getAllRules(workspaceId: string): Observable<IScannableDocumentRule[]> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments()
      .scannableDocumentsRule()
      .getApiUrl();

    return this.httpClient.get<IScannableDocumentRule[]>(url);
  }

  // POST/workspaces/:workspaceId/scannable-documents/upload
  public createScannableDocumentFromLink(workspaceId: string, request: IScannableDocumentUploadRequest): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().upload().getApiUrl();

    return this.httpClient.post<void>(url, request);
  }

  // Get/workspaces/:workspaceId/scannable-documents/download
  public getReportForDocuments(workspaceId: string): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().download().getApiUrl();

    return this.restService.download(url);
  }

  // DELETE /workspaces/:workspaceId/scannable-documents/
  public removeScannableDocument(workspaceId: string, scannableDocumentId: string | string[]): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().getApiUrl();

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: { scannableDocumentId: scannableDocumentId },
    };

    return this.httpClient.delete<void>(url, httpOptions);
  }

  // POST /workspaces/:workspaceId/scannable-documents/startDocumentScan
  public startDocumentScan(workspaceId: string, scannableDocumentId: string | string[]): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().startDocumentScan().getApiUrl();

    const body: IScannableDocumentReportQuery = {
      scannableDocumentId: scannableDocumentId,
    };

    return this.httpClient.post<void>(url, body);
  }

  // POST /workspaces/:workspaceId/scannable-documents/requestRemediation
  public requestRemediationDocumentScan(workspaceId: string, scannableDocumentIds: string[]): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().requestRemediation().getApiUrl();

    const body: IScannableDocumentReportQuery = {
      scannableDocumentId: scannableDocumentIds,
    };

    return this.httpClient.post<void>(url, body);
  }

  // DETELE /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/removeRemediation
  public removeRemediatedDocument(workspaceId: string, scannableDocumentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .removeRemediation()
      .getApiUrl();

    return this.httpClient.delete<void>(url);
  }

  // DETELE /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/revokeRemediationRequest
  public revokeRemediationRequest(workspaceId: string, scannableDocumentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .revokeRemediationRequest()
      .getApiUrl();

    return this.httpClient.delete<void>(url);
  }

  // GET /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/download-test-report
  public downloadTestReport(workspaceId: string, scannableDocumentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .downloadTestReport()
      .getApiUrl();

    return this.httpClient
      .get(url, { observe: 'response', responseType: 'arraybuffer' })
      .pipe(map(ResourceUtility.downloadResponse));
  }

  // GET /workspaces/:workspaceId/scannable-documents/pdf-pages-test-state
  public getPdfPagesTestState(workspaceId: string): Observable<IScannableDocumentPdfPagesTestStateResponse> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).scannableDocuments().pdfPagesTestState().getApiUrl();

    return this.httpClient.get<IScannableDocumentPdfPagesTestStateResponse>(url);
  }

  // GET /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/download
  public downloadDocument(workspaceId: string, scannableDocumentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .download()
      .getApiUrl();

    return this.httpClient
      .get(url, { observe: 'response', responseType: 'arraybuffer' })
      .pipe(map(ResourceUtility.downloadResponse));
  }

  // POST /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/start-auto-remediation
  public startAutoRemediation(workspaceId: string, scannableDocumentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .startAutoRemediation()
      .getApiUrl();

    return this.httpClient.post<void>(url, {});
  }

  // GET /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/auto-remediation-state
  public getAutoRemediationState(workspaceId: string, scannableDocumentId: string): Observable<IAutoRemediationStateResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .autoRemediationState()
      .getApiUrl();

    return this.httpClient.get<IAutoRemediationStateResponse>(url);
  }

  // POST /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/upload-auto-remediated-pdf
  public uploadAutoRemediatedPdf(workspaceId: string, scannableDocumentId: string): Observable<IScannableDocument> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .uploadAutoRemediatedPdf()
      .getApiUrl();

    return this.httpClient.post<IScannableDocument>(url, {});
  }

  // PUT /workspaces/:workspaceId/scannable-documents/:scannableDocumentId/set-remediation
  public setRemediatedDocument(workspaceId: string, scannableDocumentId: string, remediatedDocumentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .scannableDocuments(scannableDocumentId)
      .setRemediation()
      .getApiUrl();

    const body: IRemediatedDocumentRequest = { [$remediatedDocumentRequest.remediatedDocumentId]: remediatedDocumentId };
    return this.httpClient.put<void>(url, body);
  }
}
